/**
 * Coalesce per-card thumbnail requests into batched HTTP calls.
 *
 * Mirrors slide-sense's `use-thumbnail-lazy-request.ts`. When N
 * MediaPreviewCards mount in the same tick (gallery first paint, tab
 * switch, polling refresh) each can call `requestImageThumbnail` instead
 * of firing a single-id POST — the 50ms debounce window collects every
 * pending id and ships a single batch.
 *
 * Also exposes a viewport-aware ref via `useImageThumbnailLazyRequest`:
 * attach to a card and the request only fires once the card scrolls
 * within ~1000px of the viewport, keeping the eager prefetch from
 * downloading the whole gallery when the user only sees the first row.
 */

import { useEffect, useRef } from "react"

import { prefetchImageThumbnails, useImageThumbnailCache } from "./use-image-thumbnail-prefetch"

const FLUSH_DEBOUNCE_MS = 50
const VIEWPORT_MARGIN = "1000px"

const pendingIds = new Set<string>()
const retryTimers = new Map<string, ReturnType<typeof setTimeout>>()
let flushTimer: ReturnType<typeof setTimeout> | null = null

function scheduleFlush() {
  if (flushTimer !== null) return
  flushTimer = setTimeout(() => {
    flushTimer = null
    const ids = Array.from(pendingIds)
    pendingIds.clear()
    if (ids.length > 0) void prefetchImageThumbnails(ids)
  }, FLUSH_DEBOUNCE_MS)
}

function scheduleRetryAfterCooldown(jobId: string, cooldownMs: number) {
  if (retryTimers.has(jobId)) return
  const delayMs = Math.max(cooldownMs, FLUSH_DEBOUNCE_MS)
  const timer = setTimeout(() => {
    retryTimers.delete(jobId)
    requestImageThumbnail(jobId)
  }, delayMs)
  retryTimers.set(jobId, timer)
}

/**
 * Queue a single job id for the next batched fetch.
 *
 * Exported so non-IO callers (e.g. `useStudioImageThumbnail`'s mount-time
 * fallback when the cache is empty) can join the same 50ms debounce
 * window instead of each firing a single-id batch request.
 *
 * No-op when the job is already cached, in-flight, or known-failed.
 */
export function requestImageThumbnail(jobId: string) {
  const cache = useImageThumbnailCache.getState()
  if (cache.has(jobId) || cache.isPending(jobId) || cache.hasFailed(jobId)) return
  // Honor the transient-failure cooldown — otherwise a flaky backend would
  // get hammered repeatedly because every card mount would re-queue its id.
  const cooldownMs = cache.cooldownRemainingMs(jobId)
  if (cooldownMs > 0) {
    scheduleRetryAfterCooldown(jobId, cooldownMs)
    return
  }
  pendingIds.add(jobId)
  scheduleFlush()
}

export function useImageThumbnailLazyRequest<T extends HTMLElement = HTMLDivElement>(
  jobId: string | undefined
) {
  const ref = useRef<T | null>(null)
  useEffect(() => {
    if (!jobId) return
    const el = ref.current
    if (!el) return
    if (useImageThumbnailCache.getState().has(jobId)) return

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            requestImageThumbnail(jobId)
            observer.disconnect()
            break
          }
        }
      },
      { rootMargin: VIEWPORT_MARGIN }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [jobId])
  return ref
}
